/**
 * Row equality for the board's 15-second poll.
 *
 * The poll used to replace every project object on every tick, so every row
 * re-rendered every 15 seconds whether or not anything changed, and with a memoised
 * row that identity churn would have thrown the memo away. `sameRow` lets the poll
 * keep the previous object when the incoming one is identical.
 *
 * WHAT IS COMPARED: everything. `canonical()` serialises the whole document with keys
 * sorted at every level, so two rows are "same" only if every field the server sent,
 * nested fields and arrays included, is byte-for-byte identical. There is no field
 * list to keep in step with what the row renders: a new field the row starts reading
 * is covered the day it exists. The only way a real change slips through is if it is
 * not in the document at all, and a row cannot render what it does not have.
 *
 * Arrays compare in order (a reordered status_history is a change). undefined values
 * are dropped by JSON, so {a: undefined} equals {}; the server never sends undefined.
 */
export function canonical(value) {
  if (value === null || typeof value !== 'object') return JSON.stringify(value === undefined ? null : value);
  if (Array.isArray(value)) return '[' + value.map(canonical).join(',') + ']';
  const keys = Object.keys(value).filter(k => value[k] !== undefined).sort();
  return '{' + keys.map(k => JSON.stringify(k) + ':' + canonical(value[k])).join(',') + '}';
}

export function sameRow(prev, next) {
  if (prev === next) return true;
  if (!prev || !next) return false;
  return canonical(prev) === canonical(next);
}

/** For a setState updater: keep the previous value when the fresh one is identical in
 *  content, so a memoised row that receives it as a prop does not re-render. Plain
 *  objects, arrays and scalars only: a Set or Map serialises to {} and would always
 *  compare equal, so compare their contents yourself before building one. */
export function unlessSame(prev, next) {
  return sameRow(prev, next) ? prev : next;
}

/** Merge a fresh list into the previous one, keeping the previous object wherever the
 *  document is identical. Returns a new array (order and membership follow `next`). */
export function keepUnchanged(prev, next) {
  if (!prev || !prev.length) return next;
  const byId = new Map(prev.map(p => [p.id, p]));
  let allKept = prev.length === next.length;
  const out = next.map((n, i) => {
    const p = byId.get(n.id);
    const keep = p && sameRow(p, n);
    if (!keep || p !== prev[i]) allKept = false;
    return keep ? p : n;
  });
  return allKept ? prev : out;   // same list in the same order: keep the array too
}
